import { RasterLayerSpecification } from '../chart-props';
import { RasterDrawFunction } from './type';

type LayerState = {
    canvas: OffscreenCanvas;
};

const WIDTH = 128;
const HEIGHT = 64;

export const drawRasterLayer: RasterDrawFunction<RasterLayerSpecification, LayerState> = (pass, source, layer, xAxis, yAxis, state) => {
    if (!state || source.isDirty()) {
        const canvas = state?.canvas ?? new OffscreenCanvas(WIDTH, HEIGHT);

        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, WIDTH, HEIGHT);
        const imgData = ctx.createImageData(WIDTH, HEIGHT);

        const bounds = source.getBounds();
        const xStep = (bounds[2] - bounds[0]) / WIDTH;
        const yStep = (bounds[3] - bounds[1]) / HEIGHT;

        for (let y = 0; y < HEIGHT; y++) {
            for (let x = 0; x < WIDTH; x++) {
                const i = y * WIDTH * 4 + x * 4;
                const unitX = bounds[0] + x * xStep;
                const unitY = bounds[1] + y * yStep;

                let mappedV: number;
                if (layer.paint?.interpolate) {
                    const value = source.getValueBounds(unitX, unitY);
                    if (!value) {
                        continue;
                    }

                    if (!('bounds' in value)) {
                        mappedV = layer.getValue(value.value);
                    } else {
                        mappedV = blerp(
                            value.bounds,
                            [
                                [layer.getValue(value.values[0][0]), layer.getValue(value.values[0][1])],
                                [layer.getValue(value.values[1][0]), layer.getValue(value.values[1][1])]
                            ],
                            [unitX, unitY]
                        );
                    }
                } else {
                    const value = source.getValue(unitX, unitY);
                    if (!value) {
                        continue;
                    }
                    mappedV = layer.getValue(value);
                }

                const idx = layer.colorRamp.findIndex((x) => mappedV <= x.value);
                let color: (typeof layer.colorRamp)[0]['color'];
                if (idx === 0) {
                    color = layer.colorRamp[idx].color;
                } else {
                    const c1 = layer.colorRamp[idx - 1];
                    const c2 = layer.colorRamp[idx];
                    const a = (mappedV - c1.value) / (c2.value - c1.value);
                    color = lerp(c1.color, c2.color, a);
                }

                imgData.data[i + 0] = color[0];
                imgData.data[i + 1] = color[1];
                imgData.data[i + 2] = color[2];
                imgData.data[i + 3] = color[3];
            }
        }
        ctx.putImageData(imgData, 0, 0);

        state = { canvas };
    }

    const [l, t, r, b] = source.getBounds();

    pass.drawer.ctx.save();
    pass.drawer.ctx.globalAlpha = layer.paint?.opacity ?? 1.0;
    pass.drawer.ctx.drawImage(
        state.canvas,
        0,
        0,
        WIDTH,
        HEIGHT,
        xAxis.project(l, pass.viewState),
        yAxis.project(t, pass.viewState),
        xAxis.project(r, pass.viewState) - xAxis.project(l, pass.viewState),
        yAxis.project(b, pass.viewState) - yAxis.project(t, pass.viewState)
    );

    pass.drawer.ctx.restore();

    return state;
};

function lerp(a: [number, number, number, number], b: [number, number, number, number], t: number): [number, number, number, number] {
    // prettier-ignore
    return [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
        a[3] + (b[3] - a[3]) * t,
    ];
}

function blerp(
    [[x1, y1], [x2, y2]]: [[number, number], [number, number]],
    [[x1y1v, x1y2v], [x2y1v, x2y2v]]: [[number, number], [number, number]],
    [x, y]: [number, number]
) {
    const q11 = (((x2 - x) * (y2 - y)) / ((x2 - x1) * (y2 - y1))) * x1y1v;
    const q21 = (((x - x1) * (y2 - y)) / ((x2 - x1) * (y2 - y1))) * x2y1v;
    const q12 = (((x2 - x) * (y - y1)) / ((x2 - x1) * (y2 - y1))) * x1y2v;
    const q22 = (((x - x1) * (y - y1)) / ((x2 - x1) * (y2 - y1))) * x2y2v;
    return q11 + q21 + q12 + q22;
}
